export interface CuratedStory {
  id: string;
  source: 'atc_image' | 'atc_episode';
  text: string;
  gender: string;
  age: string;
  frequency: string;
}

const CURATED_STORY_IDS = ['2391', '1575', '1810', '2357'] as const;
const CURATED_STORY_KEYS = ['id', 'source', 'text', 'gender', 'age', 'frequency'] as const;

const SENSITIVE_TEXT_PATTERNS = [
  /(^|\D)\d{7}(\D|$)/,
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /(?:0\d{1,4}[-ー－]?\d{1,4}[-ー－]?\d{3,4})/,
] as const;

function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(`curated stories: ${message}`);
}

function assertExactKeys(value: Record<string, unknown>, index: number): void {
  const keys = Object.keys(value).sort();
  const expected = [...CURATED_STORY_KEYS].sort();
  invariant(
    keys.length === expected.length && keys.every((key, keyIndex) => key === expected[keyIndex]),
    `[${index}] に許可されていない、または不足している項目があります`,
  );
}

/**
 * Validates the deliberately narrow story artifact before it reaches a
 * presentation component. Postal data and other voice metadata are excluded by
 * the exact-key boundary; common contact details are also rejected in text.
 */
export function assertCuratedStories(value: unknown): readonly CuratedStory[] {
  invariant(Array.isArray(value), 'ルートが配列ではありません');
  invariant(value.length === CURATED_STORY_IDS.length, '4件ではありません');

  const seen = new Set<string>();
  value.forEach((story, index) => {
    invariant(typeof story === 'object' && story !== null && !Array.isArray(story), `[${index}] が不正です`);
    const record = story as Record<string, unknown>;
    assertExactKeys(record, index);

    invariant(typeof record.id === 'string' && CURATED_STORY_IDS.includes(record.id as (typeof CURATED_STORY_IDS)[number]), `[${index}].id が不正です`);
    invariant(!seen.has(record.id), `[${index}].id が重複しています`);
    seen.add(record.id);
    invariant(record.source === 'atc_image' || record.source === 'atc_episode', `[${index}].source が不正です`);
    invariant(typeof record.text === 'string' && record.text.trim().length > 0, `[${index}].text が不正です`);
    invariant(
      !SENSITIVE_TEXT_PATTERNS.some((pattern) => pattern.test(record.text as string)),
      `[${index}].text に個人情報の可能性がある文字列が含まれています`,
    );
    for (const field of ['gender', 'age', 'frequency'] as const) {
      invariant(typeof record[field] === 'string' && record[field].trim().length > 0, `[${index}].${field} が不正です`);
    }
  });

  invariant(CURATED_STORY_IDS.every((id) => seen.has(id)), '必要なエピソードが不足しています');
  return value as CuratedStory[];
}

export const CURATED_STORIES = assertCuratedStories([
  {
    id: '2391',
    source: 'atc_episode',
    text: '18歳のとき、ここからフェリーで1人でバイク旅に九州に出かけました。\nあれから約10年。\n子供もでき家族でくる日が来るとは。',
    gender: '男性',
    age: '20代',
    frequency: '月に1回程度',
  },
  {
    id: '1575',
    source: 'atc_image',
    text: '私が小さい頃から父とよく来る場所で\nとても思い出深い場所です。\nここに来ると懐かしい気持ちが蘇り、\n幸せを感じます。\n私の人生になくてはならない場所だったなと、思っています。\n大学も相愛大学で、あの頃も毎日のように来ていました。\nポテトのお店が懐かしく思います。\n色々なお店がありましたが\nほとんどなくなってしまい\n少し寂しいですが、ATCはここにあり続けてくれていて\nそれだけで幸せ。\nそう感じて、今でも月に何度か訪れます。\n今は小さかった私も結婚し\n夫と娘ときています。\nたまに、昔のように父をつれて。',
    gender: '女性',
    age: '30代',
    frequency: '月に2～3回程度',
  },
  {
    id: '1810',
    source: 'atc_episode',
    text: 'たまたま夫と同じ場所(勤務先は異なります)に勤めているので、時間が合う日は一緒にATCに入っているカフェやレストランで昼ごはんを食べます。海を眺めながらどこのレストランに行くか話す瞬間が好きです。\nイベントをやっている時は、今度から行こっか？と話しながら決めるのが楽しいです。',
    gender: '女性',
    age: '30代',
    frequency: 'ほぼ毎日',
  },
  {
    id: '2357',
    source: 'atc_episode',
    text: 'いつもNMB48のイベントやメーカーの展示会等でよく来るのですが、イベント自体にもたくさんの思い出がありますが、イベント終わりに仲間とATCの飲食店でワイワイ語り合いながら飲み会をするのが楽しみで来ています。\n孫も生まれたので、孫がもう少し大きくなったら、プラレール博や恐竜博等のイベントに孫と一緒に来たいです。',
    gender: '男性',
    age: '50代',
    frequency: '年に2～3回程度',
  },
] satisfies CuratedStory[]);
